import { DOCUMENT } from '@angular/common';
import { Injectable, inject, NgZone } from '@angular/core';
import { LocalStorageService } from '@cdw-ae/local-storage';
import { Observable } from 'rxjs';
import { SESSION_TIMEOUT_CONFIG } from '../../../+state/app-lifecycles/session-timeout.config';

/**
 * FR-1: mouse movement, clicks, keystrokes, scrolling and touch all count as
 * activity. Background work does not — which needs no code, because we listen
 * to DOM input events and never to HTTP traffic.
 */
const ACTIVITY_EVENT_TYPES = [
  'mousemove',
  'mousedown',
  'keydown',
  'wheel',
  'scroll',
  'touchstart',
] as const;

/**
 * `capture: true` matters: scroll events do not bubble, so a document-level
 * listener would otherwise miss scrolling inside the device grid's viewport.
 * `passive: true` because we never call preventDefault.
 */
const LISTENER_OPTIONS: AddEventListenerOptions = {
  capture: true,
  passive: true,
};

/**
 * Shared with every other Gopher tab in this browser profile.
 *
 * Deliberately not namespaced by user id. A key left behind by a crashed tab
 * can only ever hold a timestamp older than the current session's start, and
 * the reducer takes `Math.max`, so a stale value is inert. Namespacing would
 * buy nothing and would mean plumbing the uid into this service.
 */
const PEER_ACTIVITY_KEY = 'gopher.session.lastActivityAt';
const PEER_EVENT_KEY = 'gopher.session.event';

/** What a peer tab has told us. */
export type PeerSessionEvent =
  | { type: 'activity'; atMs: number }
  | { type: 'extended'; atMs: number }
  | { type: 'terminated' };

/**
 * EDUS-2655 — the impure edge of the inactivity timeout.
 *
 * This service holds no state and makes no decisions. Its job is to turn raw
 * browser input — DOM events, and `storage` events from other tabs — into
 * streams of plain data; when the session starts and stops, what counts as
 * "too long", and what happens at the threshold are all decided in the store.
 */
@Injectable({ providedIn: 'root' })
export class SessionActivityService {
  private readonly zone = inject(NgZone);
  private readonly document = inject(DOCUMENT);
  private readonly config = inject(SESSION_TIMEOUT_CONFIG);

  /**
   * Cold stream of activity timestamps — listeners attach on subscribe and
   * detach on unsubscribe, so the caller controls the lifecycle.
   *
   * Listeners are registered OUTSIDE the Angular zone. The app runs zone-based
   * change detection with event coalescing, so an in-zone `mousemove` listener
   * would schedule a change-detection pass per frame and visibly degrade the
   * device search grid (AC 1.1). We re-enter the zone only on the throttled
   * emission — at most once per `activityThrottleMs` — which keeps everything
   * downstream (timers, dialog opening) in the zone where Angular expects it.
   */
  readonly userActivity$: Observable<number> = new Observable<number>(
    (subscriber) =>
      this.zone.runOutsideAngular(() => {
        let lastEmitAt = 0;

        const onActivity = (): void => {
          const now = Date.now();
          // A plain timestamp comparison rather than an RxJS operator: it runs
          // per raw DOM event, so it must stay allocation-free and must not
          // schedule anything.
          if (now - lastEmitAt < this.config.activityThrottleMs) {
            return;
          }
          lastEmitAt = now;
          // Publish to peer tabs from here rather than from an effect, so
          // only genuinely local input is broadcast. Echoing peer-sourced
          // activity back out would have the tabs volleying indefinitely.
          try {
            LocalStorageService.set(PEER_ACTIVITY_KEY, String(now));
          } catch {
            // Storage full or blocked — activity tracking continues; peer tabs miss this tick.
          }
          this.zone.run(() => subscriber.next(now));
        };

        for (const type of ACTIVITY_EVENT_TYPES) {
          this.document.addEventListener(type, onActivity, LISTENER_OPTIONS);
        }

        return () => {
          for (const type of ACTIVITY_EVENT_TYPES) {
            this.document.removeEventListener(
              type,
              onActivity,
              LISTENER_OPTIONS
            );
          }
        };
      })
  );

  /**
   * What other tabs are doing.
   *
   * `storage` fires in every same-origin document *except* the one that wrote,
   * which makes it a cross-tab broadcast with no extra plumbing. Preferred
   * over BroadcastChannel because localStorage is durable: a tab the browser
   * froze or discarded missed every message ever broadcast, but can still read
   * the current value when it comes back (see `readPeerActivity`).
   */
  readonly peerEvents$: Observable<PeerSessionEvent> =
    new Observable<PeerSessionEvent>((subscriber) =>
      this.zone.runOutsideAngular(() => {
        const onStorage = (event: StorageEvent): void => {
          const peerEvent = parsePeerEvent(event);
          if (peerEvent) {
            this.zone.run(() => subscriber.next(peerEvent));
          }
        };

        this.window?.addEventListener('storage', onStorage);
        return () => this.window?.removeEventListener('storage', onStorage);
      })
    );

  /**
   * Tell peer tabs the session was extended or ended.
   *
   * The payload carries a sequence number because writing a byte-identical
   * value does not reliably fire `storage` — two terminations in the same
   * millisecond would otherwise reach peers as one.
   */
  publish(type: 'extended' | 'terminated'): void {
    this.publishSequence += 1;
    LocalStorageService.set(
      PEER_EVENT_KEY,
      JSON.stringify({
        type,
        atMs: Date.now(),
        sequence: this.publishSequence,
      })
    );
  }

  /**
   * The shared activity timestamp as it stands right now.
   *
   * Read on wake: a frozen tab processes no `storage` events, so it can come
   * back believing it has been idle for however long it was asleep while
   * another tab was in active use (AC 3.1).
   */
  readPeerActivity(): number | null {
    const raw = LocalStorageService.get(PEER_ACTIVITY_KEY);
    if (!raw) {
      return null;
    }
    const parsed = Number(raw);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  /** Drop the shared keys so a later session starts from a clean slate. */
  clearPeerState(): void {
    LocalStorageService.remove(PEER_ACTIVITY_KEY);
    LocalStorageService.remove(PEER_EVENT_KEY);
  }

  /**
   * The last event a peer tab published, read directly rather than via the
   * `storage` event a frozen tab would have missed entirely. Checked on wake
   * alongside `readPeerActivity` — a peer's termination is a stronger signal
   * than its activity timestamp and should be acted on first (AC 3.2).
   */
  readPeerEvent(): PeerSessionEvent | null {
    return parsePeerEventPayload(
      PEER_EVENT_KEY,
      LocalStorageService.get(PEER_EVENT_KEY)
    );
  }

  private publishSequence = 0;

  private get window(): (Window & typeof globalThis) | null {
    return this.document.defaultView;
  }
}

/**
 * Anything unrecognised, malformed, or a key removal degrades to "no peer
 * information" rather than throwing inside a DOM listener.
 *
 * Shared between the live `storage` listener and a direct on-demand read
 * (`readPeerEvent`), so a value that arrived as a `StorageEvent` and one read
 * straight out of `localStorage` parse identically.
 */
function parsePeerEventPayload(
  key: string | null,
  newValue: string | null
): PeerSessionEvent | null {
  if (!newValue) {
    return null;
  }

  if (key === PEER_ACTIVITY_KEY) {
    const atMs = Number(newValue);
    return Number.isFinite(atMs) && atMs > 0
      ? { type: 'activity', atMs }
      : null;
  }

  if (key !== PEER_EVENT_KEY) {
    return null;
  }

  let payload: unknown;
  try {
    payload = JSON.parse(newValue);
  } catch {
    return null;
  }

  if (typeof payload !== 'object' || payload === null) {
    return null;
  }

  const { type, atMs } = payload as { type?: unknown; atMs?: unknown };
  if (type === 'terminated') {
    return { type: 'terminated' };
  }
  if (
    type === 'extended' &&
    typeof atMs === 'number' &&
    Number.isFinite(atMs)
  ) {
    return { type: 'extended', atMs };
  }
  return null;
}

function parsePeerEvent(event: StorageEvent): PeerSessionEvent | null {
  return parsePeerEventPayload(event.key, event.newValue);
}
